import React, { useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { AUDIENCES } from '../data/audiences';
import { setContactRole } from '../lib/leads';
import GroupRegister from './GroupRegister';

// One template for the six audience pages. Content lives in src/data/audiences.js.
export default function AudiencePage({ audience, isDark, onNavigate }) {
  const d = AUDIENCES[audience];

  useEffect(() => {
    window.scrollTo(0, 0);
    // The Services page can bring a visitor straight to a section, such as the group form.
    let target = null;
    try { target = sessionStorage.getItem('imfuyo-scroll-to'); sessionStorage.removeItem('imfuyo-scroll-to'); } catch (e) { /* ignore */ }
    if (target) setTimeout(() => { const el = document.getElementById(target); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 400);
  }, [audience]);

  if (!d) return null;

  const go = (page) => {
    if (page === 'register-group') { const el = document.getElementById('register-group'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
    if (page === 'contact') { setContactRole(audience); onNavigate('landing', 'contact'); } else onNavigate(page);
  };

  const bg = isDark ? 'bg-gray-950' : 'bg-white';
  const soft = isDark ? 'bg-gray-900' : 'bg-[#F4F7F2]';
  const head = isDark ? 'text-white' : 'text-[#14301A]';
  const body = isDark ? 'text-gray-300' : 'text-gray-600';
  const card = isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';
  const font = { fontFamily: "'Inter', system-ui, sans-serif" };

  return (
    <div className={bg} style={{ ...font, paddingTop: '80px' }}>
      {/* Hero */}
      <section className="bg-[#14301A] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <p className="text-sm font-semibold tracking-widest uppercase text-[#B7D9A8] mb-4">{d.eyebrow}</p>
          <h1 className="text-3xl sm:text-5xl font-bold leading-tight max-w-3xl">{d.title}</h1>
          <p className="mt-5 text-lg text-white/80 max-w-2xl">{d.sub}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            {d.ctas.map((c, i) => (
              <button
                key={c.label}
                onClick={() => go(c.page)}
                className={`px-6 py-3 rounded-full font-semibold transition-colors ${
                  i === 0 ? 'bg-[#D55103] hover:bg-[#B84402] text-white' : 'border border-white/50 text-white hover:bg-white/10'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {audience === 'groups' && <GroupRegister isDark={isDark} onNavigate={onNavigate} />}

      {/* What you get */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <p className="text-sm font-semibold tracking-widest uppercase text-[#285020] mb-2">What you get</p>
        <h2 className={`text-2xl sm:text-3xl font-bold mb-8 ${head}`}>Clear benefits, no confusion.</h2>
        <div className="grid gap-5 md:grid-cols-3">
          {d.benefits.map((b) => (
            <div key={b.title} className={`rounded-2xl border p-6 ${card}`}>
              <h3 className={`text-lg font-semibold mb-2 ${head}`}>{b.title}</h3>
              <p className={body}>{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Steps */}
      <section className={soft}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <p className="text-sm font-semibold tracking-widest uppercase text-[#285020] mb-2">How it works for you</p>
          <h2 className={`text-2xl sm:text-3xl font-bold mb-8 ${head}`}>Three simple steps.</h2>
          <ol className="grid gap-5 md:grid-cols-3">
            {d.steps.map((s, i) => (
              <li key={s.title} className={`rounded-2xl border p-6 ${card}`}>
                <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#285020] text-white font-bold mb-4">{i + 1}</span>
                <h3 className={`text-lg font-semibold mb-1 ${head}`}>{s.title}</h3>
                <p className={body}>{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Start here */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <p className="text-sm font-semibold tracking-widest uppercase text-[#285020] mb-2">Start here</p>
        <h2 className={`text-2xl sm:text-3xl font-bold mb-8 ${head}`}>Everything for you, in one place.</h2>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {d.links.map((l) => (
            <button
              key={l.title}
              onClick={() => go(l.page)}
              className={`group text-left rounded-2xl border p-6 transition-shadow hover:shadow-lg ${card}`}
            >
              <span className={`block text-lg font-semibold mb-1 ${head}`}>{l.title}</span>
              <span className={`block mb-4 ${body}`}>{l.text}</span>
              <span className="inline-flex items-center gap-1 text-[#285020] font-semibold">
                Open <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </button>
          ))}
        </div>
        {d.livestock && (
          <div className={`mt-8 rounded-2xl border p-6 ${card}`}>
            <h3 className={`text-lg font-semibold mb-1 ${head}`}>{d.livestock.title}</h3>
            <p className={body}>{d.livestock.text}</p>
          </div>
        )}
        <div className={`mt-8 rounded-2xl border-l-4 border-[#500808] p-5 ${isDark ? 'bg-gray-900 text-gray-200' : 'bg-[#FBF3F0] text-gray-800'}`}>
          <strong>{d.caveat.title}</strong> {d.caveat.text}
        </div>
      </section>

      {/* Closing call */}
      <section className="bg-[#285020] text-white">
        <div className="max-w-3xl mx-auto px-4 py-14 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold">Ready to get started?</h2>
          <p className="mt-3 text-white/80">Tell us who you are. We will point you to the right next step.</p>
          <button
            onClick={() => { setContactRole(audience); onNavigate('landing', 'contact'); }}
            className="mt-6 px-7 py-3 rounded-full bg-[#D55103] hover:bg-[#B84402] font-semibold"
          >
            Partner with Imfuyo
          </button>
        </div>
      </section>
    </div>
  );
}
